import { useTranslation } from "react-i18next";

import { isLocalDev } from "../../config";
import { useSimulateScan } from "../../contexts/ScannerContext";
import classNames from "../../utils/classNames";

interface ScanQrCodeProps {
  /** The exact barcode payload, e.g. "v|{id}|{vrm}" or "a|damaged". */
  value: string;
  alt: string;
  /** Pixel size requested from the QR service; the rendered size comes from `className`. */
  size?: number;
  className?: string;
}

/**
 * A scannable QR for one of our barcode payloads. When running locally it also
 * shows a "Simulate scan" button beneath, which replays the payload through the
 * scanner context exactly as a physical scan would arrive - handy for working
 * on the scan flows without a scanner plugged in.
 */
export default function ScanQrCode({ value, alt, size = 180, className }: ScanQrCodeProps) {
  const { t } = useTranslation("common");
  const simulateScan = useSimulateScan();

  const img = (
    <img
      src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`}
      alt={alt}
      className={classNames("rounded bg-white", className ?? "")}
    />
  );

  if (!isLocalDev) return img;

  return (
    <div className="flex flex-col items-center gap-1">
      {img}
      <button
        type="button"
        onClick={() => simulateScan(value)}
        title={value}
        className="rounded border border-dashed border-gray-400 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 hover:bg-gray-100"
      >
        {t("Simulate scan")}
      </button>
    </div>
  );
}
